// CDV-027: scope cascade for data blocks embedded inside a composed view.
//
// Each data block (`photos`, `analyses`, `timeline`, `events_by_hour`)
// may omit `site_id` / `camera_id`. The cascade resolves missing fields
// in this order:
//
//   block.site_id → composed.site_id → page_context.site_id → null
//   block.camera_id → composed.camera_id → page_context.camera_id → null
//
// `site_id` is required for every data block (none of the underlying
// queries work without one); `camera_id` is optional and the underlying
// body component handles its absence (site-wide vs camera-specific
// rendering).
//
// Helpers return either the fully-resolved intent shape the standalone
// body component already consumes, or null when the cascade fails.
// `BlockRenderer` then either mounts the body or renders the standard
// "block could not be displayed" note (CDV-025).

import type { Block, ComposedIntent, SalaIntent, ViewPageContext } from '@/lib/viewIntent'

type DataBlock = Extract<Block, { type: 'photos' | 'analyses' | 'timeline' | 'events_by_hour' }>
type PhotosIntent = Extract<SalaIntent, { kind: 'photos' }>
type AnalysesIntent = Extract<SalaIntent, { kind: 'analyses' }>
type TimelineIntent = Extract<SalaIntent, { kind: 'timeline' }>
type EventsByHourIntent = Extract<SalaIntent, { kind: 'events_by_hour' }>

interface Cascade {
  block: { site_id?: string; camera_id?: string }
  composed: ComposedIntent
  ctx: ViewPageContext
}

function resolveSite({ block, composed, ctx }: Cascade): string | null {
  if (block.site_id) return block.site_id
  if (composed.site_id) return composed.site_id
  if ('site_id' in ctx) return ctx.site_id
  return null
}

function resolveCamera({ block, composed, ctx }: Cascade): string | undefined {
  if (block.camera_id) return block.camera_id
  if (composed.camera_id) return composed.camera_id
  if (ctx.scope === 'camera') return ctx.camera_id
  return undefined
}

export function photosIntentFromBlock(
  block: Extract<DataBlock, { type: 'photos' }>,
  composed: ComposedIntent,
  ctx: ViewPageContext,
): PhotosIntent | null { //@usedby:composedScope.test.ts
  const siteId = resolveSite({ block, composed, ctx })
  if (!siteId) return null
  // Same constraint as the planner enforces for top-level photos: need
  // either a date or a curated event_ids list, otherwise we'd render
  // the entire camera feed forever.
  const hasIds = Array.isArray(block.event_ids) && block.event_ids.length > 0
  if (!block.date && !hasIds) return null
  const cameraId = resolveCamera({ block, composed, ctx })
  return {
    kind: 'photos',
    site_id: siteId,
    date: block.date,
    event_ids: block.event_ids,
    ...(cameraId ? { camera_id: cameraId } : {}),
  }
}

export function analysesIntentFromBlock(
  block: Extract<DataBlock, { type: 'analyses' }>,
  composed: ComposedIntent,
  ctx: ViewPageContext,
): AnalysesIntent | null { //@usedby:composedScope.test.ts
  const siteId = resolveSite({ block, composed, ctx })
  if (!siteId) return null
  const cameraId = resolveCamera({ block, composed, ctx })
  return {
    kind: 'analyses',
    site_id: siteId,
    date: block.date,
    date_from: block.date_from,
    date_to: block.date_to,
    analysis_type: block.analysis_type,
    ...(cameraId ? { camera_id: cameraId } : {}),
  }
}

export function timelineIntentFromBlock(
  block: Extract<DataBlock, { type: 'timeline' }>,
  composed: ComposedIntent,
  ctx: ViewPageContext,
): TimelineIntent | null { //@usedby:composedScope.test.ts
  const siteId = resolveSite({ block, composed, ctx })
  if (!siteId) return null
  const cameraId = resolveCamera({ block, composed, ctx })
  return {
    kind: 'timeline',
    site_id: siteId,
    date: block.date,
    date_from: block.date_from,
    date_to: block.date_to,
    ...(cameraId ? { camera_id: cameraId } : {}),
  }
}

export function eventsByHourIntentFromBlock(
  block: Extract<DataBlock, { type: 'events_by_hour' }>,
  composed: ComposedIntent,
  ctx: ViewPageContext,
): EventsByHourIntent | null { //@usedby:composedScope.test.ts
  const siteId = resolveSite({ block, composed, ctx })
  if (!siteId) return null
  // events_by_hour requires a date as a kind, but the wire schema lets
  // a block omit it (e.g. composed view about "today"). Without a date
  // we have no axis to render — surface as block error.
  if (!block.date) return null
  const cameraId = resolveCamera({ block, composed, ctx })
  return {
    kind: 'events_by_hour',
    site_id: siteId,
    date: block.date,
    ...(cameraId ? { camera_id: cameraId } : {}),
  }
}
